/**
 * Keyboard shortcuts — docs/06-UI-SPEC.md §16.
 *
 * Kept as a plain reading of a key press rather than as an event handler, so every
 * combination can be checked without a browser. What each command then does lives
 * with the thing that does it; this file only says which command a key press is.
 *
 * The calculator, the grapher and the equation editor have their own keys in §16 and
 * their own phases. Teaching Mode is Phase 8. Those keys are left unread until the
 * thing they open exists, rather than being wired to nothing.
 */

import type { ToolId } from '@opal/shared';

/** The part of a key press this file reads. */
export interface KeyLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

/** The part of whatever a key press landed on that this file reads. */
export interface TargetLike {
  readonly tagName: string;
  readonly isContentEditable: boolean;
  /** An input's type, where it has one. Absent means a plain text field. */
  readonly type?: string;
}

/**
 * Inputs that are controls rather than fields: a slider, a tick box, a colour well.
 *
 * A letter pressed at one of these is not part of a word, so the board's shortcuts
 * still apply — which is what keeps P from stopping working for the rest of the
 * lesson because the teacher last touched the thickness slider.
 */
const CONTROL_INPUTS: readonly string[] = [
  'range',
  'checkbox',
  'radio',
  'color',
  'button',
  'submit',
  'reset',
  'file',
];

/**
 * True when a key press belongs to something being typed into.
 *
 * A text box on the board is a real text field, so the space bar is a space and
 * Ctrl+Z takes back the last word — not a pan of the paper and not an undo of the
 * whole board. The board's own shortcuts stand aside while anything is being written.
 */
export function isTypingTarget(target: TargetLike): boolean {
  if (target.isContentEditable || target.tagName === 'TEXTAREA') return true;
  if (target.tagName !== 'INPUT') return false;
  return !CONTROL_INPUTS.includes((target.type ?? 'text').toLowerCase());
}

/** The same question, asked of a real key press, where the target may be anything. */
export function typingInto(target: EventTarget | null): boolean {
  // Asked where there is no browser — a test — nothing is being typed into.
  if (typeof HTMLElement === 'undefined' || !(target instanceof HTMLElement)) return false;
  return isTypingTarget(target);
}

/** SRS §16 — one press, one thing the board should do. */
export type BoardCommand =
  | { readonly kind: 'tool'; readonly tool: ToolId }
  | { readonly kind: 'undo' }
  | { readonly kind: 'redo' }
  | { readonly kind: 'copy' }
  | { readonly kind: 'cut' }
  | { readonly kind: 'paste' }
  | { readonly kind: 'duplicate' }
  | { readonly kind: 'delete' }
  | { readonly kind: 'selectAll' }
  | { readonly kind: 'zoom'; readonly direction: 'in' | 'out' | 'reset' }
  | { readonly kind: 'page'; readonly direction: 'previous' | 'next' }
  | { readonly kind: 'help' }
  | { readonly kind: 'cancel' };

/** docs/06-UI-SPEC.md §16 — a tool per letter, pressed on its own. */
const TOOL_KEYS: Readonly<Record<string, ToolId>> = {
  p: 'pen',
  h: 'highlighter',
  e: 'eraser',
  v: 'select',
  t: 'text',
  r: 'shape',
  n: 'sticky',
  l: 'laser',
};

/** Ctrl on Windows, Command on a Mac. Alt is never part of a board shortcut. */
function commanding(event: KeyLike): boolean {
  return (event.ctrlKey || event.metaKey) && !event.altKey;
}

/** The commands that need Ctrl held down. */
function withCommand(key: string, event: KeyLike): BoardCommand | null {
  if (key === 'z') return event.shiftKey ? { kind: 'redo' } : { kind: 'undo' };
  if (key === 'c') return { kind: 'copy' };
  if (key === 'x') return { kind: 'cut' };
  if (key === 'v') return { kind: 'paste' };
  if (key === 'd') return { kind: 'duplicate' };
  if (key === 'a') return { kind: 'selectAll' };
  // The plus key is Shift+= on most keyboards, and '+' on the numeric pad.
  if (key === '+' || key === '=') return { kind: 'zoom', direction: 'in' };
  if (key === '-' || key === '_') return { kind: 'zoom', direction: 'out' };
  if (key === '0') return { kind: 'zoom', direction: 'reset' };
  if (key === 'arrowleft') return { kind: 'page', direction: 'previous' };
  if (key === 'arrowright') return { kind: 'page', direction: 'next' };
  return null;
}

/** The commands pressed on their own. */
function alone(key: string, event: KeyLike): BoardCommand | null {
  const tool = TOOL_KEYS[key];
  if (tool && !event.shiftKey) return { kind: 'tool', tool };
  if (key === 'delete' || key === 'backspace') return { kind: 'delete' };
  if (key === 'escape') return { kind: 'cancel' };
  if (key === '?') return { kind: 'help' };
  return null;
}

/**
 * What a key press means on the board, or null when it means nothing.
 *
 * Nothing is read while something is being typed into: that is the caller's to check
 * with `typingInto`, because only the caller knows what the press landed on.
 */
export function readShortcut(event: KeyLike): BoardCommand | null {
  const key = event.key.toLowerCase();
  if (commanding(event)) return withCommand(key, event);
  if (event.altKey) return null;
  return alone(key, event);
}

/** One line of the sheet the `?` key opens: what to press, and what it does. */
export interface ShortcutRow {
  readonly keys: string;
  readonly action: string;
}

export interface ShortcutGroup {
  readonly title: string;
  readonly rows: readonly ShortcutRow[];
}

/**
 * docs/06-UI-SPEC.md §16 — the reference sheet, written the way the teacher reads it.
 *
 * Only keys that do something today. A sheet that promised a calculator key before
 * there is a calculator would teach the teacher something untrue, so those lines
 * arrive with the tools they belong to.
 */
export const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    title: 'Tools',
    rows: [
      { keys: 'P', action: 'Pen' },
      { keys: 'H', action: 'Highlighter' },
      { keys: 'E', action: 'Eraser' },
      { keys: 'V', action: 'Select' },
      { keys: 'R', action: 'Shapes' },
      { keys: 'T', action: 'Text' },
      { keys: 'N', action: 'Sticky note' },
      { keys: 'L', action: 'Laser pointer' },
    ],
  },
  {
    title: 'Your work',
    rows: [
      { keys: 'Ctrl + Z', action: 'Undo' },
      { keys: 'Ctrl + Shift + Z', action: 'Redo' },
      { keys: 'Ctrl + C', action: 'Copy what is selected' },
      { keys: 'Ctrl + X', action: 'Cut what is selected' },
      { keys: 'Ctrl + V', action: 'Paste' },
      { keys: 'Ctrl + D', action: 'Make a second copy of what is selected' },
      { keys: 'Ctrl + A', action: 'Select everything on this page' },
      { keys: 'Delete', action: 'Delete what is selected' },
    ],
  },
  {
    title: 'Moving around',
    rows: [
      { keys: 'Ctrl + +', action: 'Zoom in' },
      { keys: 'Ctrl + −', action: 'Zoom out' },
      { keys: 'Ctrl + 0', action: 'Back to 100%' },
      { keys: 'Space + drag', action: 'Move the board, whatever tool is in your hand' },
      { keys: 'Ctrl + ← / →', action: 'Previous page / next page' },
    ],
  },
  {
    title: 'This sheet',
    rows: [
      { keys: '?', action: 'Show these shortcuts' },
      { keys: 'Esc', action: 'Close this sheet, or let go of what is selected' },
    ],
  },
];
